import type { Permission } from '@bookora/shared';
import type { LogicalPlacement } from '@bookora/ui';

import type { TourAnchorId } from '../anchors';
import type { OwnerVariant, Persona } from '../persona';

/** Tours with steps. Keys are stored server-side; never rename one. */
export type ShippedTourKey =
  | 'core'
  | 'calendar'
  | 'session-form'
  | 'patient-file'
  | 'inventory'
  | 'crm'
  | 'accounting'
  | 'ai-import-review'
  | 'staff'
  | 'procedures'
  | 'working-hours'
  | 'patients'
  | 'dashboard'
  | 'packages'
  | 'ai-import-capture'
  | 'appointment-form'
  // Pricing build: declared now so the owning streams never edit this frozen
  // file; registered as step-less stubs (`raas-results.ts`, `branches.ts`)
  // until their steps land — a stub is never offered or linkable.
  | 'raas-results'
  | 'branches';

/**
 * Tours declared ahead of their steps. Each has a stub in `planned.ts` — no
 * steps, never offered, not linkable (`isTourKey` is false) — so a screen can
 * already mount its host or chip and it renders nothing until the tour's body
 * lands. Moving a key from here to `ShippedTourKey` is part of writing its
 * tour.
 */
export type PlannedTourKey = 'session-detail';

export type TourKey = ShippedTourKey | PlannedTourKey;

/**
 * The inline tour hosts (a form inside a drawer/dialog that registers itself
 * through `useInlineTour(key, …)`). The key is the tour's own key.
 */
export type InlineHostKey = 'session-form' | 'appointment-form' | 'session-detail';

/**
 * Feature gates a tour (or a step, `requiresFeature`) may declare: photo import
 * availability, «نتیجه‌محور», and the plan-enforced features (D6). Tours FAIL
 * CLOSED: a feature is `false` while plan access is unknown (panel-locked R15).
 */
export type TourFeature =
  | 'aiImport'
  | 'raas'
  | 'accounting'
  | 'campaigns'
  | 'loyalty'
  | 'lotInventory'
  | 'payroll'
  | 'revenueShare'
  | 'apiAccess'
  | 'branches';

/** Every tour feature off — the fail-closed default before anything is known. */
export const NO_TOUR_FEATURES: Readonly<Record<TourFeature, boolean>> = Object.freeze({
  aiImport: false,
  raas: false,
  accounting: false,
  campaigns: false,
  loyalty: false,
  lotInventory: false,
  payroll: false,
  revenueShare: false,
  apiAccess: false,
  branches: false,
});
export type Viewport = 'wide' | 'compact';

/** `all` must all be held; `any`, at least one. */
export interface Requirement {
  all?: readonly Permission[];
  any?: readonly Permission[];
}

/** An anchor id, or `nav.<section key>` (a sidebar item, through `navAnchor()`). */
export type StepAnchorId = TourAnchorId | `nav.${string}`;

export interface StepContext {
  persona: Persona;
  ownerVariant: OwnerVariant;
  permissions: readonly string[];
  viewport: Viewport;
  clinicType: 'CLINIC' | 'SOLO_DOCTOR';
  /** `null` = unknown, or the caller lacks `settings.edit`. */
  onboardingIncomplete: boolean | null;
  /**
   * The `panelPath` of the first required setup step still open (the server's
   * onboarding checklist), when `onboardingIncomplete` is true. Absent or
   * `null` = unknown; the core tour's finish then opens the checklist itself.
   */
  nextSetupPath?: string | null;
  can(p: Permission): boolean;
}

export type Text = string | ((ctx: StepContext) => string);

export interface TourStepDef {
  /** `^[a-z0-9-]+$`; the analytics id and the deep-link step id. */
  id: string;
  anchor: StepAnchorId | { wide: StepAnchorId | null; compact: StepAnchorId | null };
  placement?: LogicalPlacement | 'auto';
  requires?: Requirement;
  /** The step shows only when this tour feature is on (pricing build; resolve-steps honours it). */
  requiresFeature?: TourFeature;
  personas?: readonly Persona[];
  when?(ctx: StepContext): boolean;
  /** Steps sharing a group: the first eligible one wins. */
  group?: string;
  title: Text;
  /** Compact variants branch on `ctx.viewport`. */
  body: Text;
  spotlightPadding?: number;
}

/** Where a context tour starts when it cannot start where the user is. */
export type TourContext = 'patient' | 'session-form' | 'import-job' | 'appointment-form';

export interface TourDef {
  key: TourKey;
  /** Bump to re-offer a changed tour as «به‌روز شده». */
  version: number;
  surface: 'shell' | 'page' | 'inline';
  title: string;
  hubDescription: string;
  offer?: { body: string };
  /**
   * `match` sees the query string too (`search`, with its `?`, or `''`), so a
   * tab of a page can own a tour (`/settings?tab=hours`).
   */
  route?: { path: string; match(pathname: string, search: string): boolean; context?: TourContext };
  inlineHost?: InlineHostKey;
  /**
   * The last step's primary button (critic C7): instead of «متوجه شدم», a next
   * action — COMPLETED is recorded, the run ends, then the panel navigates to
   * `to`. Shown only when `requires` holds (and `when`, if given); otherwise
   * the tour ends as before. The only finish mechanism: no toast after a tour.
   */
  finish?: TourFinish;
  requires?: Requirement;
  requiresFeature?: TourFeature;
  /** Offered once, quietly, beside the page's chip on a qualifying visit. */
  autoOffer: boolean;
  /** ≤ 5. */
  maxSteps: number;
  /** Default 2. */
  minSteps: number;
  /** Step ids in the order they are kept when more than `maxSteps` resolve. */
  priority?: readonly string[];
  steps: readonly TourStepDef[];
}

export interface TourFinish {
  label: Text;
  /** A panel path, may carry a query (`/calendar?new=1`). */
  to: string | ((ctx: StepContext) => string);
  requires?: Requirement;
  when?(ctx: StepContext): boolean;
}

export function textOf(text: Text, ctx: StepContext): string {
  return typeof text === 'function' ? text(ctx) : text;
}
